import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined } from '@ant-design/icons';
import apiClient from '../api/client';

const { Text, Paragraph } = Typography;

// Are the check targets themselves still working?
//
// Every proxy verdict this system produces is a statement about a request to
// one of the target URLs configured above. That makes them a shared point of
// failure: if one stops answering, or starts answering something other than the
// expected content, every proxy in the fleet is reported broken at once and
// nothing in the health data says otherwise.
//
// Each worker therefore asks the targets directly — no proxy in between — when
// it starts and every 30 minutes after. This panel puts those answers side by
// side, because the comparison is the diagnosis: one worker failing to reach a
// target is that worker's route out, every worker failing to reach it is the
// target.

interface TargetState {
  url: string;
  alive: boolean;
  dead?: boolean;
  failedProbes?: number;
  latencyMs?: number;
  error?: string;
  contentChecked?: boolean;
  checkedAt?: string;
  deadSince?: string;
}

interface FleetRow {
  url: string;
  verdict: 'ok' | 'worker' | 'dead' | 'failing' | 'unknown';
  alive: number;
  dead: number;
  // Probed, currently failing, not yet failing often enough to be called dead.
  failing: number;
  // How many workers have an opinion at all — the denominator. One worker
  // saying "I cannot reach it" is not the same claim as six saying it.
  reporting: number;
  deadOn?: string[];
  error?: string;
  lastCheckedAt?: string;
}

interface WorkerRow {
  workerId: string;
  name: string;
  country?: string;
  online: boolean;
  targets?: TargetState[];
}

const VERDICT: Record<FleetRow['verdict'], { color: string; text: string }> = {
  ok: { color: 'success', text: 'answering every worker' },
  worker: { color: 'warning', text: 'failing on some workers' },
  dead: { color: 'error', text: 'answering nobody' },
  failing: { color: 'warning', text: 'failing, not yet confirmed' },
  unknown: { color: 'default', text: 'not measured yet' },
};

const TargetHealthPanel: React.FC<{ configured?: string[] }> = ({ configured = [] }) => {
  const [fleet, setFleet] = useState<FleetRow[]>([]);
  const [workers, setWorkers] = useState<WorkerRow[]>([]);
  // What the workers have actually been told to stop asking, as opposed to what
  // the summary concludes. They agree except for a moment around a change.
  const [disabled, setDisabled] = useState<string[]>([]);
  // Starts true: the panel always fetches on mount, and a false start renders
  // the "nobody has reported" empty state for a frame before the data lands.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/admin/workers/targets');
      setFleet(data.data?.fleet || []);
      setWorkers(data.data?.workers || []);
      setDisabled(data.data?.disabled || []);
      setError(null);
    } catch (e: any) {
      // Clear the numbers as well: a stale table under a fresh error reads as
      // current data.
      setFleet([]);
      setWorkers([]);
      setDisabled([]);
      setError(e.response?.data?.error || 'Could not load target health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // "dead" already requires that at least two workers independently failed to
  // reach it — one worker's opinion never gets to convict a target, because
  // during a rollout it may be the only one reporting.
  const deadEverywhere = fleet.filter((f) => f.verdict === 'dead');
  // Kept apart on purpose: "some workers fail it" means the target is up and
  // those routes are not, while "every reporting worker failed it" means the
  // opposite. One sentence cannot honestly describe both.
  const failingOnSome = fleet.filter((f) => f.verdict === 'worker');
  const failingOnAll = fleet.filter((f) => f.verdict === 'failing');
  const measured = workers.filter((w) => (w.targets?.length || 0) > 0);

  // A target nobody has probed yet has no row of its own — rows are built from
  // what workers reported. Show it anyway, so a URL just added above does not
  // simply vanish from this table.
  const rows: FleetRow[] = [
    ...fleet,
    ...configured
      .filter((url) => url && !fleet.some((f) => f.url === url))
      .map((url) => ({
        url, verdict: 'unknown' as const, alive: 0, dead: 0, failing: 0, reporting: 0,
      })),
  ];

  const columns: ColumnsType<FleetRow> = [
    {
      title: 'Target',
      dataIndex: 'url',
      key: 'url',
      ellipsis: { showTitle: false },
      render: (url: string) => (
        <Text ellipsis={{ tooltip: url }} style={{ fontSize: 12 }}>{url}</Text>
      ),
    },
    {
      title: 'Verdict',
      key: 'verdict',
      width: 190,
      render: (_: unknown, row: FleetRow) => {
        const v = VERDICT[row.verdict] || VERDICT.unknown;
        return (
          <Space size={4}>
            <Tag color={v.color} style={{ marginInlineEnd: 0 }}>{v.text}</Tag>
            {disabled.includes(row.url) && (
              <Tooltip title="Removed from checks — every reporting worker failed it">
                <Tag color="error" style={{ marginInlineEnd: 0 }}>removed</Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Workers',
      key: 'workers',
      width: 210,
      render: (_: unknown, row: FleetRow) => (
        <Tooltip title={row.deadOn?.length ? `fails on: ${row.deadOn.join(', ')}` : undefined}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
            <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
              {row.reporting === 0
                ? 'nobody has reported on it'
                : `${row.alive} of ${row.reporting} reach it`}
            </Text>
            {!!row.deadOn?.length && (
              <Tag color="error" style={{ marginInlineEnd: 0, flexShrink: 0 }}>
                {row.deadOn.length === 1 ? row.deadOn[0] : `${row.deadOn.length} fail`}
              </Tag>
            )}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Last failure',
      dataIndex: 'error',
      key: 'error',
      ellipsis: { showTitle: false },
      render: (err: string, row: FleetRow) => (
        <Tooltip title={[err, row.lastCheckedAt ? `last probed ${new Date(row.lastCheckedAt).toLocaleString()}` : '']
          .filter(Boolean).join(' · ') || undefined}>
          <Text type="secondary" style={{ fontSize: 12 }}>{err || '—'}</Text>
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 6 }} align="baseline">
        <Text strong style={{ fontSize: 13 }}>Are these targets alive?</Text>
        <Button size="small" icon={<ReloadOutlined />} loading={loading} onClick={load}>Refresh</Button>
      </Space>
      <Paragraph
        type="secondary"
        style={{ fontSize: 12, marginBottom: 8 }}
        ellipsis={{ rows: 1, expandable: 'collapsible', symbol: (expanded) => (expanded ? 'less' : 'why this matters') }}
      >
        Every worker asks these URLs directly, with no proxy in between, on start and every 30 minutes.
        A target that stops answering would otherwise fail every proxy check at once, for reasons that
        have nothing to do with the proxies. One worker failing to reach a target changes nothing about
        its checks — it keeps trying it in place, and the other targets already cover it. A target is
        removed from checks only when every reporting worker has failed it.
      </Paragraph>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 8 }} />}

      {deadEverywhere.length > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 8 }}
          message={`${deadEverywhere.length} target(s) answer no worker at all`}
          description={
            <>
              {deadEverywhere.map((f) => f.url).join(', ')} — every worker that reported failed to reach
              this, from different countries, so it is the target rather than one machine's route.
              {deadEverywhere.length < fleet.length
                ? ' Checks now try it last instead of first, so it no longer costs every check its timeout.'
                : ' Every configured target is in this state, so checks keep trying all of them — a check'
                  + ' that requested nothing would report every proxy as healthy. Treat those verdicts with care.'}
              {' '}Fix or replace it above; it returns to normal use on its own once it answers again.
            </>
          }
        />
      )}

      {failingOnSome.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 8 }}
          message={`${failingOnSome.length} target(s) are failing on some workers`}
          description={
            <>
              {failingOnSome.map((f) => `${f.url} (${f.deadOn?.join(', ')})`).join('; ')} — other workers
              reach it, so the target is up and those machines' routes to it are not. Nothing changes for
              their checks: they keep trying it in its configured place, and the other targets cover it.
            </>
          }
        />
      )}

      {failingOnAll.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 8 }}
          message={`${failingOnAll.length} target(s) failed their last probe on every reporting worker`}
          description={
            <>
              {failingOnAll.map((f) => `${f.url} (${f.reporting} worker${f.reporting === 1 ? '' : 's'} reporting)`).join('; ')}
              {' '}— nobody reached it just now, but not enough workers have failed it twice for the fleet
              to call it dead. If this is the start of an outage the next probe will confirm it; if it was
              a blip it clears on its own.
            </>
          }
        />
      )}

      {measured.length === 0 && !loading && !error && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 8 }}
          message="No worker has reported on the targets yet"
          description="Workers report this from the build that introduced it. One that has not been updated yet runs its checks exactly as before and simply says nothing here."
        />
      )}

      <Table<FleetRow>
        size="small"
        rowKey="url"
        tableLayout="fixed"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
      />

      {measured.length > 0 && (
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
          Reported by {measured.length} worker(s): {measured.map((w) => w.name).join(', ')}
        </Text>
      )}
    </div>
  );
};

export default TargetHealthPanel;
